import { Button, Card, CardContent, Stack } from "@mui/material";
import { useRouter } from "next/router";
import { useState, useCallback, memo, useEffect } from "react";
import { GREY } from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import usePersistedStore from "@/store/usePersistedStore";
import useOnlineStorePackagesStore from "@/store/views/onlineStore/onlineStorePackagesStore";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";
import PackageCardContent from "./packageCardContent";
import PackageCardHeader from "./packageCardHeader";
import PackageDetailsModal from "./packageDetailsModal";
import PackageStoreButton from "./packageStoreButton";

type PackageCardProps = {
  packageItem: MedspaPackage;
  isFeaturedDeal?: boolean;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  medspaId: string;
  setIsCheckoutDrawerOpen: (isOpen: boolean) => void;
};

const CARD_HEIGHT = 355;
const CARD_HEIGHT_MOBILE = 320;

const PackageCard = memo(function PackageCard({
  packageItem,
  isFeaturedDeal,
  customizationEnabled,
  customizationPrimaryColor,
  medspaId,
  setIsCheckoutDrawerOpen,
}: PackageCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useRouter();
  const { packageId } = router.query;
  const store = useOnlineStorePackagesStore(medspaId);
  const { changePackageQuantity, getPackageQuantity, setPackageQuantity } =
    store.getState();

  const packagesQuantity = usePersistedStore(
    store,
    (state) => state.packagesQuantity
  );
  const packageQuantity = getPackageQuantity(packageItem.id);
  const isSmallScreen = useMoxieMediaQuery("ipad-ver", "down");

  const {
    endDate,
    packageName,
    packagePrice,
    packageItems,
    packageSavings,
    additionalInfo,
  } = packageItem;

  const thumbnailUrl = additionalInfo?.thumbnail ?? "/assets/images/gift.png";

  const onAmountChange = useCallback(
    (change: -1 | 1) => {
      changePackageQuantity(packageItem.id, change);
    },
    [changePackageQuantity, packageItem.id]
  );

  const onInputChange = useCallback(
    (value: string) => {
      setPackageQuantity(packageItem.id, +value);
    },
    [setPackageQuantity, packageItem.id]
  );

  const openPackageModal = useCallback(() => {
    setIsModalOpen(true);
    router.replace(
      { query: { ...router.query, packageId: packageItem.id } },
      undefined,
      { shallow: true }
    );
  }, [router, packageItem.id]);

  const handleCardClick = useCallback(() => {
    openPackageModal();
  }, [openPackageModal]);

  const handleViewDetailsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openPackageModal();
    },
    [openPackageModal]
  );

  const handleModalClose = useCallback(() => {
    if (router?.query?.packageId) {
      const newQuery = { ...router.query };
      delete newQuery.packageId;
      router.replace({ query: newQuery }, undefined, { shallow: true });
    }

    setIsModalOpen(false);
  }, [router]);

  const isPackageInCart = packageQuantity > 0;

  useEffect(() => {
    if (packageId === packageItem.id) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [packageId, packageItem.id]);

  // Required to avoid Next.js `Text content does not match server-rendered HTML` error when using persist middleware
  if (!packagesQuantity) return null;

  const getCardHeight = () => {
    if (isSmallScreen) return CARD_HEIGHT_MOBILE;
    return CARD_HEIGHT;
  };

  return (
    <>
      <Card
        sx={{
          width: "auto",
          height: getCardHeight(),
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: GREY[20],
          borderRadius: "16px !important",
          cursor: "pointer",
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          },
        }}
        onClick={handleCardClick}
      >
        <CardContent sx={{ p: "0 !important", height: "100%" }}>
          <Stack justifyContent="space-between" height="100%">
            <Stack p={1} spacing={1}>
              <PackageCardHeader
                thumbnailUrl={thumbnailUrl}
                packageSavings={Number(packageSavings)}
                isFeaturedDeal={isFeaturedDeal}
                endDate={endDate}
                isMobile={isSmallScreen}
              />
              <PackageCardContent
                packageName={packageName}
                packagePrice={Number(packagePrice)}
                packageSavings={Number(packageSavings)}
                packageItems={packageItems}
                isMobile={isSmallScreen}
              />
            </Stack>
            <Stack
              sx={{
                px: 1.5,
                pb: 1.5,
                gap: 1,
                justifyContent: "space-between",
                flexDirection: "row",
              }}
            >
              <PackageStoreButton
                fullWidth={isSmallScreen}
                packageQuantity={packageQuantity}
                onAmountChange={onAmountChange}
                onInputChange={onInputChange}
                unitPrice={Number(packagePrice)}
                isMobile={isSmallScreen}
                customizationEnabled={customizationEnabled}
                customizationPrimaryColor={customizationPrimaryColor}
              />
              {!isPackageInCart && !isSmallScreen && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleViewDetailsClick}
                >
                  View Details
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <PackageDetailsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onClose={handleModalClose}
        packageItem={packageItem}
        isFeaturedDeal={isFeaturedDeal}
        packageQuantity={packageQuantity}
        onAmountChange={onAmountChange}
        onInputChange={onInputChange}
        customizationEnabled={customizationEnabled}
        customizationPrimaryColor={customizationPrimaryColor}
        setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
      />
    </>
  );
});

export default PackageCard;
